import { DocumentsResponseErrorDto } from '../types/documents-response-dto';

import { getCarrierFromErrorCode, isErrorCodeOf } from './carrier-error-code';
import {
  FILE_GENERATION_ERROR,
  INVALID_AGENCY_ERROR,
  MISSING_TAX_INFORMATION_ERROR,
  JET_CPF_SENDER_ERROR,
  DISPATCH_LIMIT_PER_DAY_ERROR,
  UNMAPPED_CORREIOS_ERROR,
  MISSING_STATE_REGISTRATION_ERROR,
  ALREADY_IN_PROCESSING_ERROR,
  MARK_ORDER_AS_SENT_ERROR,
  DISPATCHER_ERROR,
  CARRIER_WITH_PASS_THROUGH_ERRORS,
  MISSING_FISCAL_INFORMATION_DOCUMENT_ERROR,
  CANNOT_PRINT_CONTENT_DECLARATION_NO_DCE_ERROR,
  CORREIOS_DCE_POLLING_EXHAUSTED_ERROR,
  DCE_NOT_ALLOWED_INTERSTATE_ROUTE_ERROR,
  DCE_INELEGIBLE_MERCHANT
} from './constants';

const isFiscalDocumentErrorCode = (code: string) =>
  code === MISSING_FISCAL_INFORMATION_DOCUMENT_ERROR ||
  code === DCE_INELEGIBLE_MERCHANT ||
  isErrorCodeOf(code, DCE_NOT_ALLOWED_INTERSTATE_ROUTE_ERROR);

export enum ErrorType {
  FILE_GENERATION_ERROR = 'FILE_GENERATION_ERROR',
  STATUS_CHANGE = 'STATUS_CHANGE',
  INVALID_AGENCY = 'INVALID_AGENCY',
  MISSING_FISCAL_INFORMATION = 'MISSING_FISCAL_INFORMATION',
  UNEXPECTED = 'UNEXPECTED',
  DISPATCH_LIMIT_PER_DAY_ERROR = 'DISPATCH_LIMIT_PER_DAY_ERROR',
  UNMAPPED_CORREIOS_ERROR = 'UNMAPPED_CORREIOS_ERROR',
  MISSING_STATE_REGISTRATION_ERROR = 'MISSING_STATE_REGISTRATION_ERROR',
  ALREADY_IN_PROCESSING_ERROR = 'ALREADY_IN_PROCESSING_ERROR',
  MARK_ORDER_AS_SENT_ERROR = 'MARK_ORDER_AS_SENT_ERROR',
  DISPATCHER_ERROR = 'DISPATCHER_ERROR',
  REPRINT_ERROR = 'REPRINT_ERROR',
  PASS_THROUGH_DISPATCH_ERROR = 'PASS_THROUGH_DISPATCH_ERROR',
  MISSING_FISCAL_INFORMATION_DOCUMENT_ERROR = 'MISSING_FISCAL_INFORMATION_DOCUMENT_ERROR',
  CANNOT_PRINT_CONTENT_DECLARATION_NO_DCE_ERROR = 'CANNOT_PRINT_CONTENT_DECLARATION_NO_DCE_ERROR',
  CORREIOS_DCE_POLLING_EXHAUSTED_ERROR = 'CORREIOS_DCE_POLLING_EXHAUSTED_ERROR',
  CORREIOS_DCE_INELEGIBLE_MERCHANT = 'CORREIOS_DCE_INELEGIBLE_MERCHANT',
  JET_CPF_SENDER_ERROR = 'JET_CPF_SENDER_ERROR'
}

export type ErrorValues = {
  [k: string]: string | number | string[] | undefined;
};

function isErrorCode(errors: DocumentsResponseErrorDto[], errorCode?: string) {
  return errors?.some((error) => error?.message?.code === errorCode);
}

export const isPassThroughError = (errors: DocumentsResponseErrorDto[]) => {
  return errors?.some((error) => {
    const isCarrierWithPassThroughErrors =
      CARRIER_WITH_PASS_THROUGH_ERRORS.includes(error?.carrierCode);
    const isCarrierError = error?.message?.code?.includes(error?.carrierCode);

    return isCarrierWithPassThroughErrors && isCarrierError;
  });
};

export const getPassThroughErrorMessage = (
  errors: DocumentsResponseErrorDto[]
) => {
  return errors?.find((error) => isPassThroughError([error]))?.message?.default;
};

export type DceNotAllowedByOrder = Record<string, string>;

type ErrorTypeResult = {
  type: ErrorType;
  values?: ErrorValues;
  dceNotAllowedByOrder?: DceNotAllowedByOrder;
};

const getFiscalDocumentErrorType = (
  errors: DocumentsResponseErrorDto[]
): ErrorTypeResult => {
  const missingFiscalErrors = errors.filter((error) =>
    isFiscalDocumentErrorCode(error.message.code)
  );
  const dceNotAllowedByOrder = missingFiscalErrors
    .filter(
      (error) =>
        error.deliveryOrderId &&
        isErrorCodeOf(
          error.message.code,
          DCE_NOT_ALLOWED_INTERSTATE_ROUTE_ERROR
        )
    )
    .reduce<DceNotAllowedByOrder>(
      (acc, error) => ({
        ...acc,
        [error.deliveryOrderId]:
          getCarrierFromErrorCode(error.message.code) ?? ''
      }),
      {}
    );
  const isOnlyDceIneligibleMerchant = missingFiscalErrors.every(
    (error) => error.message.code === DCE_INELEGIBLE_MERCHANT
  );

  return {
    type: isOnlyDceIneligibleMerchant
      ? ErrorType.CORREIOS_DCE_INELEGIBLE_MERCHANT
      : ErrorType.MISSING_FISCAL_INFORMATION_DOCUMENT_ERROR,
    values: {
      deliveryOrderIds: missingFiscalErrors.map(
        (error) => error.deliveryOrderId
      ),
      code: missingFiscalErrors.map((error) => error.message.code)
    },
    dceNotAllowedByOrder
  };
};

export const getErrorType = (
  errors: DocumentsResponseErrorDto[]
): ErrorTypeResult => {
  if (isErrorCode(errors, INVALID_AGENCY_ERROR))
    return {
      type: ErrorType.INVALID_AGENCY
    };

  if (isErrorCode(errors, MISSING_TAX_INFORMATION_ERROR))
    return {
      type: ErrorType.MISSING_FISCAL_INFORMATION
    };

  if (isErrorCode(errors, JET_CPF_SENDER_ERROR))
    return {
      type: ErrorType.JET_CPF_SENDER_ERROR
    };

  if (isErrorCode(errors, FILE_GENERATION_ERROR))
    return {
      type: ErrorType.FILE_GENERATION_ERROR
    };

  if (isErrorCode(errors, DISPATCH_LIMIT_PER_DAY_ERROR))
    return {
      type: ErrorType.DISPATCH_LIMIT_PER_DAY_ERROR,
      values: {
        limitPerDay:
          errors?.find(
            (error) => error.message.code === DISPATCH_LIMIT_PER_DAY_ERROR
          )?.message.values?.limitPerDay ?? 1
      }
    };

  if (isErrorCode(errors, UNMAPPED_CORREIOS_ERROR))
    return {
      type: ErrorType.UNMAPPED_CORREIOS_ERROR,
      values: {
        reason: errors.find(
          (error) => error.message.code === UNMAPPED_CORREIOS_ERROR
        )?.message.default
      }
    };

  if (isErrorCode(errors, MISSING_STATE_REGISTRATION_ERROR))
    return {
      type: ErrorType.MISSING_STATE_REGISTRATION_ERROR
    };

  if (isErrorCode(errors, ALREADY_IN_PROCESSING_ERROR))
    return {
      type: ErrorType.ALREADY_IN_PROCESSING_ERROR
    };

  if (isErrorCode(errors, MARK_ORDER_AS_SENT_ERROR))
    return {
      type: ErrorType.MARK_ORDER_AS_SENT_ERROR
    };

  if (isErrorCode(errors, CANNOT_PRINT_CONTENT_DECLARATION_NO_DCE_ERROR))
    return {
      type: ErrorType.CANNOT_PRINT_CONTENT_DECLARATION_NO_DCE_ERROR
    };

  if (isErrorCode(errors, CORREIOS_DCE_POLLING_EXHAUSTED_ERROR)) {
    const dcePoolingErrors = errors.filter(
      (error) => error.message.code === CORREIOS_DCE_POLLING_EXHAUSTED_ERROR
    );

    return {
      type: ErrorType.CORREIOS_DCE_POLLING_EXHAUSTED_ERROR,
      values: {
        deliveryOrderIds: dcePoolingErrors.map(
          (error) => error.deliveryOrderId
        ),
        code: dcePoolingErrors.map((error) => error.message.code)
      }
    };
  }

  if (errors?.some((error) => isFiscalDocumentErrorCode(error?.message?.code)))
    return getFiscalDocumentErrorType(errors);

  if (isErrorCode(errors, DISPATCHER_ERROR))
    return {
      type: ErrorType.DISPATCHER_ERROR,
      values: {
        carrierCode: errors[0]?.carrierCode
      }
    };

  if (isPassThroughError(errors))
    return {
      type: ErrorType.PASS_THROUGH_DISPATCH_ERROR,
      values: {
        default: getPassThroughErrorMessage(errors)
      }
    };

  return {
    type: ErrorType.UNEXPECTED,
    values: {
      code: errors?.[0]?.message?.code
    }
  };
};
